import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Card,
  CardBody,
  Progress,
  useDisclosure,
  Chip,
  Tabs,
  Tab,
  Autocomplete,
  AutocompleteItem,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  processExcelFile,
  fileToBase64,
  SettlementDetail,
} from 'common/src/api/excel-process';
import { processPdfFile, PdfShipmentContext } from 'common/src/api/pdf-process';
import { ExtractedSettlementCostLine } from 'common/src/api/receiver-costs';
import { Species, ExporterReceiver } from 'common/src/utils/types';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import { toast } from 'react-toastify';
import { supabase } from 'common/src/supabase';
import UploadSettlementDialog from './upload-settlement-dialog';
import { getReceiversByExporter } from 'common/src/api/exporterReceivers';
import { AddClientModal } from '../settings/AddClientModal';
import * as XLSX from 'xlsx';
import useShipmentsStore from '../../store/useShipmentsStore';
import { InboxClaim } from 'common/src/api/settlement-inbox';
import { useTranslation } from 'common/src/hooks/useTranslation';

type ReceiverFormat = {
  id: number;
  name: string;
  created_at: string;
  receiver_id: number;
  image_url?: string | null;
};

type UploadTab = 'excel' | 'pdf';

type ShipmentLookupOption = {
  key: string;
  label: string;
  description: string;
  reference_number?: string | null;
  container_number?: string | null;
  shipment_id?: number | string | null;
  boxes: ReturnType<typeof useShipmentsStore.getState>['shipments'];
};

interface ExcelUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  inboxClaim?: InboxClaim;
}

const ExcelUploadModal: React.FC<ExcelUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  inboxClaim,
}) => {
  const { t } = useTranslation('excel-upload');
  const { userProfile } = useUserStore();
  const { species, selectedSpecies: selectedSpeciesFromStore } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { shipments, loading: shipmentsLoading } = useShipmentsStore();
  const [activeTab, setActiveTab] = useState<UploadTab>('excel');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(
    selectedSpeciesFromStore ?? null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [selectedPdfShipmentKey, setSelectedPdfShipmentKey] =
    useState<string>('');
  const [selectedReceiver, setSelectedReceiver] = useState<string>('');
  const [automatedReceivers, setAutomatedReceivers] = useState<string[]>([]);
  const [exporterReceivers, setExporterReceivers] = useState<
    ExporterReceiver[]
  >([]);
  // Map of normalized alias/name -> canonical receiver name
  const [aliasToCanonical, setAliasToCanonical] = useState<
    Record<string, string>
  >({});
  // Canonical name resolved from selection (based on aliasToCanonical)
  const [selectedCanonicalReceiver, setSelectedCanonicalReceiver] =
    useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [processedData, setProcessedData] = useState<SettlementDetail[] | null>(
    null
  );
  const [processedCostLines, setProcessedCostLines] = useState<
    ExtractedSettlementCostLine[]
  >([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const {
    isOpen: isAddClientOpen,
    onOpen: onAddClientOpen,
    onClose: onAddClientClose,
  } = useDisclosure();
  const [selectedFormat, setSelectedFormat] = useState<string>('');
  const [receiverFormatsMap, setReceiverFormatsMap] = useState<
    Record<string, ReceiverFormat[]>
  >({});
  const [useAI, setUseAI] = useState(false);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingColors =
    activeTab === 'pdf'
      ? {
          background: '#fef2f2',
          light: '#fecaca',
          border: '#fca5a5',
          primary: '#dc2626',
          secondary: '#ef4444',
          accent: '#f87171',
        }
      : {
          background: '#f0fdf4',
          light: '#bbf7d0',
          border: '#86efac',
          primary: '#16a34a',
          secondary: '#22c55e',
          accent: '#4ade80',
        };

  const loadingSteps = [
    {
      message: t(
        activeTab === 'pdf'
          ? 'Leyendo archivo PDF...'
          : 'Leyendo archivo Excel...'
      ),
      icon:
        activeTab === 'pdf' ? (
          <Icon icon='tabler:file-type-pdf' className='h-7 w-7 animate-pulse motion-reduce:animate-none text-red-600 dark:text-red-400' />
        ) : (
          <svg width='28' height='28' viewBox='0 0 28 28' fill='none'>
            <rect
              x='5'
              y='1'
              width='18'
              height='26'
              rx='2'
              fill='#f0fdf4'
              stroke='#16a34a'
              strokeWidth='1.5'
            />
            <line
              x1='9'
              y1='7'
              x2='19'
              y2='7'
              stroke='#bbf7d0'
              strokeWidth='1.5'
            />
            <line
              x1='9'
              y1='11'
              x2='19'
              y2='11'
              stroke='#bbf7d0'
              strokeWidth='1.5'
            />
            <line
              x1='9'
              y1='15'
              x2='16'
              y2='15'
              stroke='#bbf7d0'
              strokeWidth='1.5'
            />
            <line
              x1='9'
              y1='19'
              x2='18'
              y2='19'
              stroke='#bbf7d0'
              strokeWidth='1.5'
            />
            <rect
              x='5'
              y='3'
              width='18'
              height='2.5'
              rx='1'
              fill='#16a34a'
              opacity='0.5'
            >
              <animate
                attributeName='y'
                values='3;24;3'
                dur='2s'
                repeatCount='indefinite'
              />
            </rect>
          </svg>
        ),
    },
    {
      message: t('Identificando estructura de datos...'),
      icon: (
        <svg width='28' height='28' viewBox='0 0 28 28' fill='none'>
          {[0, 1, 2].map((r) =>
            [0, 1, 2].map((c) => (
              <rect
                key={`${r}-${c}`}
                x={2 + c * 8.5}
                y={2 + r * 8.5}
                width='7'
                height='7'
                rx='1.5'
                fill={loadingColors.light}
                stroke={loadingColors.border}
                strokeWidth='0.5'
              >
                <animate
                  attributeName='fill'
                  values={`${loadingColors.light};${loadingColors.primary};${loadingColors.light}`}
                  dur='2s'
                  begin={`${(r * 3 + c) * 0.2}s`}
                  repeatCount='indefinite'
                />
              </rect>
            ))
          )}
        </svg>
      ),
    },
    {
      message: t('Extrayendo información de costos...'),
      icon: (
        <svg width='28' height='28' viewBox='0 0 28 28' fill='none'>
          <rect
            x='4'
            y='10'
            width='20'
            height='16'
            rx='2'
            fill={loadingColors.background}
            stroke={loadingColors.primary}
            strokeWidth='1.5'
          />
          <line
            x1='4'
            y1='15'
            x2='24'
            y2='15'
            stroke={loadingColors.light}
            strokeWidth='1'
          />
          <circle cx='9' cy='20' r='2.5' fill={loadingColors.primary}>
            <animate
              attributeName='cy'
              values='20;2;20'
              dur='2.5s'
              repeatCount='indefinite'
            />
            <animate
              attributeName='opacity'
              values='1;0;1'
              dur='2.5s'
              repeatCount='indefinite'
            />
          </circle>
          <circle cx='19' cy='18' r='2' fill={loadingColors.secondary}>
            <animate
              attributeName='cy'
              values='18;0;18'
              dur='2s'
              begin='0.6s'
              repeatCount='indefinite'
            />
            <animate
              attributeName='opacity'
              values='1;0;1'
              dur='2s'
              begin='0.6s'
              repeatCount='indefinite'
            />
          </circle>
          <circle cx='14' cy='22' r='1.5' fill={loadingColors.accent}>
            <animate
              attributeName='cy'
              values='22;4;22'
              dur='3s'
              begin='1.2s'
              repeatCount='indefinite'
            />
            <animate
              attributeName='opacity'
              values='1;0;1'
              dur='3s'
              begin='1.2s'
              repeatCount='indefinite'
            />
          </circle>
        </svg>
      ),
    },
    {
      message: t('Procesando filas de detalle...'),
      icon: (
        <svg width='28' height='28' viewBox='0 0 28 28' fill='none'>
          {[0, 1, 2, 3, 4].map((i) => (
            <rect
              key={i}
              x='2'
              y={2 + i * 5}
              width='0'
              height='3.5'
              rx='1.5'
              fill={
                [
                  loadingColors.primary,
                  loadingColors.secondary,
                  loadingColors.primary,
                  loadingColors.accent,
                  loadingColors.secondary,
                ][i]
              }
            >
              <animate
                attributeName='width'
                values='0;24;24;0'
                dur='2.5s'
                begin={`${i * 0.35}s`}
                repeatCount='indefinite'
              />
            </rect>
          ))}
        </svg>
      ),
    },
    {
      message: t('Validando y formateando resultados...'),
      icon: (
        <svg width='28' height='28' viewBox='0 0 28 28' fill='none'>
          <circle
            cx='14'
            cy='14'
            r='12'
            stroke={loadingColors.primary}
            strokeWidth='2'
            strokeDasharray='75.4'
            strokeDashoffset='75.4'
            strokeLinecap='round'
          >
            <animate
              attributeName='stroke-dashoffset'
              values='75.4;0;0;75.4'
              dur='2.5s'
              repeatCount='indefinite'
            />
          </circle>
          <polyline
            points='8,14 12,19 20,9'
            fill='none'
            stroke={loadingColors.primary}
            strokeWidth='2.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeDasharray='20'
            strokeDashoffset='20'
          >
            <animate
              attributeName='stroke-dashoffset'
              values='20;0;0;20'
              dur='2.5s'
              begin='0.8s'
              repeatCount='indefinite'
            />
          </polyline>
        </svg>
      ),
    },
  ];

  const completedIcon = (
    <svg width='28' height='28' viewBox='0 0 28 28' fill='none'>
      <circle
        cx='14'
        cy='14'
        r='12'
        fill='#f0fdf4'
        stroke='#16a34a'
        strokeWidth='2'
      />
      <polyline
        points='8,14 12,19 20,9'
        fill='none'
        stroke='#16a34a'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );

  const pendingIcon = (
    <svg width='28' height='28' viewBox='0 0 28 28' fill='none'>
      <circle
        cx='14'
        cy='14'
        r='12'
        fill='none'
        stroke='#d1d5db'
        strokeWidth='1.5'
        strokeDasharray='4 3'
      />
    </svg>
  );

  const shipmentLookupOptions = useMemo<ShipmentLookupOption[]>(() => {
    const normalizeText = (value?: string | number | null) =>
      String(value || '').trim();
    const normalizeContainer = (value?: string | number | null) =>
      normalizeText(value)
        .replace(/[-/\s]+/g, '')
        .toUpperCase();

    const filteredShipments = shipments.filter((box) => {
      const hasLookupValue = Boolean(
        normalizeText(box.reference_number) ||
          normalizeText(box.container_number)
      );
      if (!hasLookupValue) return false;

      if (
        selectedSpecies?.id &&
        box.species_id &&
        Number(box.species_id) !== selectedSpecies.id
      ) {
        return false;
      }

      if (
        selectedSeason &&
        selectedSeason !== 'all' &&
        box.season &&
        box.season !== selectedSeason
      ) {
        return false;
      }

      return true;
    });

    const grouped = new Map<string, typeof filteredShipments>();

    filteredShipments.forEach((box) => {
      const reference = normalizeText(box.reference_number);
      const container = normalizeText(box.container_number);
      const key = reference
        ? `ref:${reference.toUpperCase()}`
        : `container:${normalizeContainer(container)}`;
      const current = grouped.get(key) || [];
      current.push(box);
      grouped.set(key, current);
    });

    return Array.from(grouped.entries())
      .map(([key, boxes]) => {
        const first = boxes[0];
        const reference = normalizeText(first.reference_number);
        const container = normalizeText(first.container_number);
        const receiver = normalizeText(first.receiver);
        const ship = normalizeText(first.ship_name);
        const label = reference
          ? `Ref ${reference}${container ? ` · ${container}` : ''}`
          : `Contenedor ${container}`;
        const details = [receiver, ship, `${boxes.length} líneas`].filter(
          Boolean
        );

        return {
          key,
          label,
          description: details.join(' · '),
          reference_number: reference || null,
          container_number: container || null,
          shipment_id: first.shipment_id || null,
          boxes,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [shipments, selectedSeason, selectedSpecies?.id]);

  const selectedPdfShipment = useMemo(
    () =>
      shipmentLookupOptions.find(
        (option) => option.key === selectedPdfShipmentKey
      ),
    [shipmentLookupOptions, selectedPdfShipmentKey]
  );

  useEffect(() => {
    if (
      selectedPdfShipmentKey &&
      !shipmentLookupOptions.some(
        (option) => option.key === selectedPdfShipmentKey
      )
    ) {
      setSelectedPdfShipmentKey('');
    }
  }, [selectedPdfShipmentKey, shipmentLookupOptions]);

  const buildPdfShipmentContext = (): PdfShipmentContext | undefined => {
    if (!selectedPdfShipment) return undefined;

    return {
      reference_number: selectedPdfShipment.reference_number || null,
      container_number: selectedPdfShipment.container_number || null,
      shipment_id: selectedPdfShipment.shipment_id || null,
      packingList: selectedPdfShipment.boxes.map((box) => ({
        id: box.id,
        shipment_id: box.shipment_id,
        reference_number: box.reference_number || null,
        container_number: box.container_number || null,
        receiver: box.receiver || null,
        ship_name: box.ship_name || null,
        ship_etd: box.ship_etd || null,
        ship_eta: box.ship_eta || null,
        variety: box.variety || null,
        labeled_variety: box.labeled_variety || null,
        caliber: box.caliber || null,
        color: box.color || null,
        category: box.category || null,
        box_format: box.box_format || null,
        box_type: box.box_type || null,
        box_label: box.box_label || null,
        box_weight: box.box_weight || null,
        quantity: box.quantity || null,
        pallet: box.pallet || null,
        producer_name: box.producer_name || null,
      })),
    };
  };

  useEffect(() => {
    if (!isProcessing) {
      setLoadingMessageIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) =>
        prev < loadingSteps.length - 1 ? prev + 1 : prev
      );
    }, 10000);
    return () => clearInterval(interval);
  }, [isProcessing]);

  const augmentedExporterReceivers = [
    { id: -1, name: 'Crear nuevo cliente', exporter_id: 0, created_at: '' },
    ...exporterReceivers,
  ];

  useEffect(() => {
    if (!selectedSpecies && selectedSpeciesFromStore) {
      setSelectedSpecies(selectedSpeciesFromStore);
    }
  }, [selectedSpeciesFromStore, selectedSpecies]);

  const handleFileSelect = (file: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (
      !validTypes.includes(file.type) &&
      !file.name.toLowerCase().endsWith('.xlsx') &&
      !file.name.toLowerCase().endsWith('.xls')
    ) {
      toast.error(
        'Por favor selecciona un archivo Excel válido (.xlsx o .xls)'
      );
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande. Máximo 10MB.');
      return;
    }

    setSelectedFile(file);

    // Reset and detect workbook sheet names
    setSheetNames([]);
    setSelectedSheet('');
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const names = workbook.SheetNames || [];
          if (!names.length) {
            toast.error('El archivo Excel no contiene hojas válidas.');
            return;
          }
          setSheetNames(names);
          if (names.length === 1) setSelectedSheet(names[0]);
        } catch (err) {
          console.error('Error leyendo hojas del Excel:', err);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error('Error inicializando lectura del archivo:', err);
    }
  };

  const handlePdfFileSelect = (file: File) => {
    if (
      file.type !== 'application/pdf' &&
      !file.name.toLowerCase().endsWith('.pdf')
    ) {
      toast.error('Por favor selecciona un archivo PDF válido (.pdf)');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('El archivo PDF es demasiado grande. Máximo 20MB.');
      return;
    }

    setSelectedPdfFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handlePdfDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handlePdfFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handlePdfFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handlePdfFileSelect(files[0]);
    }
  };

  const handleProcessExcel = async () => {
    if (
      !selectedFile ||
      (!selectedReceiver && !useAI) ||
      !userProfile?.exporter_id ||
      !selectedSpecies
    ) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    const formats =
      receiverFormatsMap[selectedCanonicalReceiver || selectedReceiver] || [];
    const needsFormat = formats.length >= 1;
    if (needsFormat && !selectedFormat) {
      toast.error('Por favor selecciona un formato');
      return;
    }

    // If multiple sheets detected, require a selection
    if (sheetNames.length > 1 && !selectedSheet) {
      toast.error('Por favor selecciona la hoja del Excel a procesar');
      return;
    }

    setIsProcessing(true);
    try {
      // Convert file to base64; if multiple sheets, rebuild workbook with only selected sheet
      let base64Data: string;
      if (sheetNames.length > 1 && selectedSheet) {
        const data = new Uint8Array(await selectedFile.arrayBuffer());
        const fullWb = XLSX.read(data, { type: 'array' });
        const ws = fullWb.Sheets[selectedSheet];
        if (!ws) throw new Error('No se encontró la hoja seleccionada.');
        const newWb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(newWb, ws, selectedSheet);
        base64Data = XLSX.write(newWb, { bookType: 'xlsx', type: 'base64' });
      } else {
        base64Data = await fileToBase64(selectedFile);
      }

      // Always send the official/canonical receiver name to backend for robot matching
      const receiverName = selectedCanonicalReceiver || selectedReceiver;
      const result = await processExcelFile({
        excelData: base64Data,
        filename: selectedFile.name,
        receiverName: needsFormat ? selectedFormat : receiverName,
        exporterId: userProfile.exporter_id,
        speciesId: selectedSpecies.id,
        useAI: useAI,
      });

      if (result.success && result.data) {
        toast.success(`Excel procesado exitosamente.`);
        setProcessedData(result.data);
        setProcessedCostLines(result.costLines || []);
        setIsUploadDialogOpen(true);
      } else {
        toast.error(result.error || 'Error al procesar el archivo Excel');
      }
    } catch (error) {
      console.error('Error processing Excel file:', error);
      toast.error('Error al procesar el archivo Excel');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProcessPdf = async () => {
    if (!selectedPdfFile || !userProfile?.exporter_id || !selectedSpecies) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setIsProcessing(true);
    try {
      const base64Data = await fileToBase64(selectedPdfFile);
      const result = await processPdfFile({
        pdfData: base64Data,
        filename: selectedPdfFile.name,
        exporterId: userProfile.exporter_id,
        speciesId: selectedSpecies.id,
        shipmentContext: buildPdfShipmentContext(),
      });

      if (result.success && result.data?.length) {
        toast.success('PDF procesado exitosamente.');
        result.warnings?.forEach((warning) => toast.warning(warning));
        setProcessedData(result.data);
        setProcessedCostLines(result.costLines || []);
        setIsUploadDialogOpen(true);
      } else {
        toast.error(result.error || 'Error al procesar el archivo PDF');
      }
    } catch (error) {
      console.error('Error processing PDF file:', error);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Error al procesar el archivo PDF'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setActiveTab('excel');
    setSelectedFile(null);
    setSelectedPdfFile(null);
    setSelectedPdfShipmentKey('');
    setSelectedReceiver('');
    setSelectedSpecies(null);
    setUseAI(false);
    setIsProcessing(false);
    setProcessedData(null);
    setProcessedCostLines([]);
    setIsUploadDialogOpen(false);
    setSheetNames([]);
    setSelectedSheet('');
    onClose();
  };

  const handleUploadSuccess = () => {
    onSuccess();
    handleClose();
  };

  const removeFile = () => {
    setSelectedFile(null);
    setSheetNames([]);
    setSelectedSheet('');
  };

  const removePdfFile = () => {
    setSelectedPdfFile(null);
  };

  const fetchReceiversData = async () => {
    if (!userProfile?.exporter_id) return;
    try {
      // Fetch automated receivers
      const { data: automatedData, error: automatedError } = await supabase
        .from('receivers')
        .select('name, alternative_names, receiver_formats(*)');
      if (automatedError) throw automatedError;

      const formatsMap = (automatedData || []).reduce(
        (acc: Record<string, ReceiverFormat[]>, r: any) => {
          acc[r.name] = r.receiver_formats || [];
          return acc;
        },
        {} as Record<string, ReceiverFormat[]>
      );

      setReceiverFormatsMap(formatsMap);

      // Build alias -> canonical map (normalized to trim+uppercase)
      const aliasMap: Record<string, string> = {};
      (automatedData || []).forEach((r: any) => {
        const canonical = String(r.name || '').trim();
        if (!canonical) return;
        const norm = canonical.toUpperCase();
        aliasMap[norm] = canonical;
        const alts: string[] = Array.isArray(r.alternative_names)
          ? r.alternative_names
          : [];
        alts.forEach((alt: string) => {
          const n = String(alt || '').trim();
          if (!n) return;
          aliasMap[n.toUpperCase()] = canonical;
        });
      });
      setAliasToCanonical(aliasMap);

      const automatedNames = (automatedData || []).map((r: any) => r.name);
      setAutomatedReceivers(automatedNames);

      // Fetch exporter-specific receivers
      const exporterReceiversData = await getReceiversByExporter(
        userProfile.exporter_id
      );

      // Sort receivers: automated (canonical or alias) first, then alphabetically
      exporterReceiversData.sort((a, b) => {
        const aIsAutomated = Boolean(aliasMap[a.name.trim().toUpperCase()]);
        const bIsAutomated = Boolean(aliasMap[b.name.trim().toUpperCase()]);
        if (aIsAutomated !== bIsAutomated) {
          return aIsAutomated ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });

      setExporterReceivers(exporterReceiversData);
    } catch (error) {
      console.error('Error fetching receivers data:', error);
      toast.error('Error al cargar la lista de clientes.');
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReceiversData();
    }
  }, [userProfile, isOpen]);

  useEffect(() => {
    if (!inboxClaim) return;
    // Preload only: let the user review species, shipment context and sheet
    // before explicitly starting extraction with Procesar y Previsualizar.
    if (inboxClaim.file.name.toLowerCase().endsWith('.pdf')) {
      setActiveTab('pdf');
      handlePdfFileSelect(inboxClaim.file);
    } else {
      setActiveTab('excel');
      setUseAI(true);
      handleFileSelect(inboxClaim.file);
    }
  }, [inboxClaim]);

  useEffect(() => {
    setSelectedFormat('');
    if (selectedCanonicalReceiver) {
      const formats = receiverFormatsMap[selectedCanonicalReceiver] || [];
      if (formats.length === 1) {
        setSelectedFormat(formats[0].name);
      }
    }
  }, [selectedCanonicalReceiver, receiverFormatsMap]);

  // Resolve canonical receiver name whenever selection or alias map changes
  useEffect(() => {
    if (!selectedReceiver) {
      setSelectedCanonicalReceiver('');
      return;
    }
    const norm = selectedReceiver.trim().toUpperCase();
    const canonical = aliasToCanonical[norm] || selectedReceiver;
    setSelectedCanonicalReceiver(canonical);
  }, [selectedReceiver, aliasToCanonical]);

  const isAutomatedName = useMemo(() => {
    return (name: string) =>
      Boolean(aliasToCanonical[name.trim().toUpperCase()]);
  }, [aliasToCanonical]);

  return (
    <>
      <Modal
        isOpen={isOpen && !isUploadDialogOpen}
        onClose={handleClose}
        size='2xl'
        isDismissable={!isProcessing}
        hideCloseButton={isProcessing}
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <h3 className='text-xl font-bold'>Subir liquidación</h3>
            {/* <p className='text-sm text-default-500'>
              {activeTab === 'excel'
                ? 'Selecciona un archivo Excel y el cliente correspondiente'
                : 'Selecciona un archivo PDF'}
            </p> */}
          </ModalHeader>

          <ModalBody>
            <div className='space-y-4'>
              <Tabs
                isDisabled={Boolean(inboxClaim)}
                selectedKey={activeTab}
                onSelectionChange={(key) => {
                  if (!isProcessing) {
                    setActiveTab(key as UploadTab);
                  }
                }}
                aria-label='Tipo de archivo'
              >
                <Tab key='excel' title='Excel' />
                <Tab key='pdf' title='PDF' />
              </Tabs>

              {activeTab === 'excel' ? (
                <>
                  {/* File Upload Area */}
                  <div>
                    <label className='block text-sm font-medium mb-2'>
                      Archivo Excel *
                    </label>
                    {!selectedFile ? (
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                          dragActive
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-divider hover:border-default-400'
                        }`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                      >
                        <Icon icon='tabler:file-spreadsheet' className='mx-auto h-12 w-12 text-green-600 dark:text-green-400 mb-4' />
                        <p className='text-sm text-default-500 mb-2'>
                          Arrastra un archivo Excel aquí, o{' '}
                          <label className='text-primary-600 hover:text-primary-500 cursor-pointer'>
                            selecciona uno
                            <input
                              type='file'
                              className='hidden'
                              accept='.xlsx,.xls'
                              onChange={handleFileInput}
                              disabled={isProcessing}
                            />
                          </label>
                        </p>
                        <p className='text-xs text-default-400'>
                          Archivos .xlsx y .xls hasta 10MB
                        </p>
                      </div>
                    ) : (
                      <Card>
                        <CardBody className='p-4'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center space-x-3'>
                              <Icon icon='tabler:file-spreadsheet' className='h-8 w-8 text-green-600 dark:text-green-400' />
                              <div>
                                <p className='text-sm font-medium'>
                                  {selectedFile.name}
                                </p>
                                <p className='text-xs text-default-400'>
                                  {(selectedFile.size / 1024 / 1024).toFixed(2)}{' '}
                                  MB
                                </p>
                              </div>
                            </div>
                            <Button
                              isIconOnly
                              size='sm'
                              variant='light'
                              onClick={removeFile}
                              disabled={isProcessing || Boolean(inboxClaim)}
                            >
                              <Icon icon='tabler:x' />
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    )}
                  </div>

                  {/* Receiver Selection */}
                  <div>
                    <Select
                      items={augmentedExporterReceivers}
                      label='Cliente'
                      placeholder='Selecciona un cliente'
                      selectedKeys={selectedReceiver ? [selectedReceiver] : []}
                      onSelectionChange={(keys) => {
                        const value = Array.from(keys)[0] as string;
                        if (value === 'Crear nuevo cliente') {
                          onAddClientOpen();
                          setSelectedReceiver('');
                        } else {
                          setSelectedReceiver(value || '');
                        }
                      }}
                      isDisabled={isProcessing}
                      // description='Los clientes sin lectura automatizada se leen con IA (1-2 minutos): revisa los datos antes de guardar. ¿No ves a tu cliente en la lista? Agrégalo directamente en el selector en "Crear nuevo cliente".'
                    >
                      {(item) => (
                        <SelectItem
                          key={item.name}
                          value={item.name}
                          textValue={item.name}
                          startContent={
                            item.name === 'Crear nuevo cliente' ? (
                              <Icon icon='tabler:plus' />
                            ) : undefined
                          }
                        >
                          {item.name === 'Crear nuevo cliente' ? (
                            item.name
                          ) : (
                            <div className='flex items-center justify-between w-full'>
                              <span>{item.name}</span>
                              <Chip
                                size='sm'
                                variant='flat'
                                color={
                                  isAutomatedName(item.name)
                                    ? 'success'
                                    : 'warning'
                                }
                              >
                                {isAutomatedName(item.name)
                                  ? 'Lee en 1-10 segundos'
                                  : 'Lee en 1-2 minutos'}
                              </Chip>
                            </div>
                          )}
                        </SelectItem>
                      )}
                    </Select>
                    {/* <div className="flex items-center gap-2 mt-3">
                  <Switch
                    isSelected={useAI}
                    onValueChange={setUseAI}
                    isDisabled={isProcessing}
                  />
                  <span className="text-sm text-default-600">
                    Usar AI si no hay transformación disponible
                  </span>
                </div> */}
                  </div>

                  {/* Sheet selection when multiple sheets are detected */}
                  {selectedFile && sheetNames.length > 1 && (
                    <div>
                      <Select
                        label='Hoja de Excel'
                        placeholder='Selecciona la hoja a procesar'
                        selectedKeys={selectedSheet ? [selectedSheet] : []}
                        onSelectionChange={(keys) => {
                          const value = Array.from(keys)[0] as string;
                          setSelectedSheet(value || '');
                        }}
                        isDisabled={isProcessing}
                        description='El archivo contiene múltiples hojas. Selecciona cuál procesar.'
                      >
                        {sheetNames.map((name) => (
                          <SelectItem key={name} value={name}>
                            {name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  )}

                  {selectedCanonicalReceiver &&
                    receiverFormatsMap[selectedCanonicalReceiver]?.length >=
                      1 && (
                      <div className='flex gap-4 items-start'>
                        <div className='flex-1'>
                          <Select
                            label='Formato'
                            placeholder='Selecciona un formato'
                            selectedKeys={
                              selectedFormat ? [selectedFormat] : []
                            }
                            onSelectionChange={(keys) => {
                              const value = Array.from(keys)[0] as string;
                              setSelectedFormat(value);
                            }}
                            isDisabled={isProcessing}
                          >
                            {receiverFormatsMap[selectedCanonicalReceiver].map(
                              (format) => (
                                <SelectItem
                                  key={format.name}
                                  value={format.name}
                                >
                                  {format.name}
                                </SelectItem>
                              )
                            )}
                          </Select>
                        </div>
                        {/* Image preview for selected format */}
                        {(() => {
                          const selectedFormatObj = receiverFormatsMap[
                            selectedCanonicalReceiver
                          ]?.find((f) => f.name === selectedFormat);
                          if (selectedFormatObj?.image_url) {
                            return (
                              <div className='flex-1'>
                                <p className='text-xs text-default-400 mb-2'>
                                  Vista previa del formato
                                </p>
                                <div
                                  className='relative group cursor-pointer'
                                  onClick={() =>
                                    setEnlargedImageUrl(
                                      selectedFormatObj.image_url!
                                    )
                                  }
                                >
                                  <img
                                    src={selectedFormatObj.image_url}
                                    alt={`Vista previa de ${selectedFormat}`}
                                    className='w-full max-h-64 object-contain rounded-md border border-divider transition-all duration-200 group-hover:brightness-75'
                                  />
                                  <div className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
                                    <div className='bg-black/60 rounded-full p-3'>
                                      <Icon icon='tabler:arrows-maximize' className='text-white text-xl' />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}

                  {/* Species Selection */}
                  <div>
                    <Select
                      label='Especie'
                      selectedKeys={[selectedSpecies?.id?.toString() || '1']}
                      onSelectionChange={(keys) => {
                        const value = Array.from(keys)[0] as string;
                        setSelectedSpecies(
                          species.find(
                            (specie) => specie.id.toString() === value
                          ) || null
                        );
                      }}
                      isDisabled={isProcessing}
                    >
                      {species.map((specie: Species) => (
                        <SelectItem
                          key={specie.id.toString()}
                          value={specie.id.toString()}
                        >
                          {specie.name}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className='block text-sm font-medium mb-2'>
                      Archivo PDF *
                    </label>
                    {!selectedPdfFile ? (
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                          dragActive
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-divider hover:border-default-400'
                        }`}
                        onDrop={handlePdfDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                      >
                        <Icon icon='tabler:file-type-pdf' className='mx-auto h-12 w-12 text-red-600 dark:text-red-400 mb-4' />
                        <p className='text-sm text-default-500 mb-2'>
                          Arrastra un archivo PDF aquí, o{' '}
                          <label className='text-primary-600 hover:text-primary-500 cursor-pointer'>
                            selecciona uno
                            <input
                              type='file'
                              className='hidden'
                              accept='.pdf,application/pdf'
                              onChange={handlePdfFileInput}
                              disabled={isProcessing}
                            />
                          </label>
                        </p>
                        <p className='text-xs text-default-400'>
                          Archivos .pdf hasta 20MB
                        </p>
                      </div>
                    ) : (
                      <Card>
                        <CardBody className='p-4'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center space-x-3'>
                              <Icon icon='tabler:file-type-pdf' className='h-8 w-8 text-red-600 dark:text-red-400' />
                              <div>
                                <p className='text-sm font-medium'>
                                  {selectedPdfFile.name}
                                </p>
                                <p className='text-xs text-default-400'>
                                  {(selectedPdfFile.size / 1024 / 1024).toFixed(
                                    2
                                  )}{' '}
                                  MB
                                </p>
                              </div>
                            </div>
                            <Button
                              isIconOnly
                              size='sm'
                              variant='light'
                              onClick={removePdfFile}
                              disabled={isProcessing || Boolean(inboxClaim)}
                            >
                              <Icon icon='tabler:x' />
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    )}
                  </div>

                  <div>
                    <Select
                      label='Especie'
                      selectedKeys={[selectedSpecies?.id?.toString() || '1']}
                      onSelectionChange={(keys) => {
                        const value = Array.from(keys)[0] as string;
                        setSelectedSpecies(
                          species.find(
                            (specie) => specie.id.toString() === value
                          ) || null
                        );
                      }}
                      isDisabled={isProcessing}
                    >
                      {species.map((specie: Species) => (
                        <SelectItem
                          key={specie.id.toString()}
                          value={specie.id.toString()}
                        >
                          {specie.name}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>

                  <Autocomplete
                    label='Despacho relacionado (opcional)'
                    placeholder='Buscar por referencia o contenedor'
                    selectedKey={selectedPdfShipmentKey || null}
                    onSelectionChange={(key) =>
                      setSelectedPdfShipmentKey(key?.toString() || '')
                    }
                    isDisabled={isProcessing || shipmentsLoading}
                    description={
                      selectedPdfShipment
                        ? 'Se enviará el packing list de este despacho como contexto para la IA.'
                        : 'Opcional: ayuda a la IA a leer variedades, calibres, formatos y cantidades.'
                    }
                  >
                    {shipmentLookupOptions.map((option) => (
                      <AutocompleteItem
                        key={option.key}
                        textValue={`${option.label} ${option.description}`}
                      >
                        <div className='flex flex-col'>
                          <span>{option.label}</span>
                          {option.description && (
                            <span className='text-xs text-default-400'>
                              {option.description}
                            </span>
                          )}
                        </div>
                      </AutocompleteItem>
                    ))}
                  </Autocomplete>

                  {!shipmentsLoading && shipmentLookupOptions.length === 0 && (
                    <p className='text-xs text-default-400'>
                      No hay despachos cargados para usar como contexto
                      opcional.
                    </p>
                  )}
                </>
              )}

              {/* Processing Progress */}
              {isProcessing && (
                <div className='space-y-3 py-2'>
                  <span
                    className='sr-only'
                    role='status'
                    aria-live='polite'
                    aria-atomic='true'
                  >
                    {loadingSteps[loadingMessageIndex].message}
                  </span>
                  {loadingSteps.map((step, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 text-sm transition-all duration-500 ${
                        i < loadingMessageIndex && activeTab === 'excel'
                          ? 'text-green-600 dark:text-green-400 opacity-100'
                          : i === loadingMessageIndex
                            ? activeTab === 'pdf'
                              ? 'text-red-600 dark:text-red-400 opacity-100 font-medium'
                              : 'text-foreground opacity-100 font-medium'
                            : 'text-default-300 opacity-40'
                      }`}
                    >
                      <div className='flex-shrink-0'>
                        {i < loadingMessageIndex && activeTab === 'excel'
                          ? completedIcon
                          : i === loadingMessageIndex
                            ? step.icon
                            : pendingIcon}
                      </div>
                      {step.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              variant='light'
              onPress={handleClose}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              onPress={
                activeTab === 'excel' ? handleProcessExcel : handleProcessPdf
              }
              disabled={
                activeTab === 'excel'
                  ? !selectedFile ||
                    (!selectedReceiver && !useAI) ||
                    isProcessing ||
                    (sheetNames.length > 1 && !selectedSheet) ||
                    (selectedCanonicalReceiver
                      ? (receiverFormatsMap[selectedCanonicalReceiver]
                          ?.length || 0) >= 1 && !selectedFormat
                      : false)
                  : !selectedPdfFile ||
                    !selectedSpecies ||
                    !userProfile?.exporter_id ||
                    isProcessing
              }
              startContent={!isProcessing ? <Icon icon='tabler:upload' /> : undefined}
              isLoading={isProcessing}
            >
              {isProcessing ? 'Procesando...' : 'Procesar y Previsualizar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <UploadSettlementDialog
        isManualUpload={false}
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        excelData={processedData}
        costLines={processedCostLines}
        onSuccess={handleUploadSuccess}
        selectedSpecies={selectedSpecies}
        preferredReceiverName={selectedReceiver}
        sourceFile={activeTab === 'excel' ? selectedFile : selectedPdfFile}
        inboxClaim={inboxClaim}
      />
      {userProfile?.exporter_id && (
        <AddClientModal
          isOpen={isAddClientOpen}
          onClose={onAddClientClose}
          onClientAdded={() => {
            fetchReceiversData();
            onAddClientClose();
          }}
          exporterId={userProfile.exporter_id}
          existingClientNames={exporterReceivers.map((r) => r.name)}
          onClientAddedName={(name) => {
            // Every client can be processed (robot or AI reading).
            setSelectedReceiver(name);
          }}
        />
      )}

      {/* Enlarged Image Modal */}
      <Modal
        isOpen={!!enlargedImageUrl}
        onClose={() => setEnlargedImageUrl(null)}
        size='5xl'
        classNames={{
          wrapper: 'items-center',
          body: 'p-2',
        }}
      >
        <ModalContent>
          <ModalBody>
            {enlargedImageUrl && (
              <img
                src={enlargedImageUrl}
                alt='Vista previa ampliada del formato'
                className='w-full max-h-[80vh] object-contain'
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExcelUploadModal;
